import { utils as ejsUtils } from '@tangibleplay/ecommerce-js';

import { fieldNames } from '../AddressForm.config';
import { AddressFormProps, FieldNamesType } from '../AddressForm.types';

const addressFieldsToCheck = [
  fieldNames.FIRST_NAME,
  fieldNames.LAST_NAME,
  fieldNames.ADDRESS_1,
  fieldNames.ADDRESS_2,
  fieldNames.CITY,
  fieldNames.STATE,
  fieldNames.ZIPCODE,
  fieldNames.COUNTRY,
];

interface ErrorType {
  required?: boolean;
  format?: boolean;
  unsupportedCharacters?: boolean;
  houseNumber?: boolean;
  invalidRegion?: boolean;
  message?: string;
}

type ErrorsType = {
  [key in FieldNamesType]?: ErrorType;
};

const countriesCharactersRestrictions: {
  [key: string]: {
    regex: RegExp;
    error: ErrorType;
  };
} = {
  KR: {
    regex:
      /[\uac00-\ud7af]|[\u1100-\u11ff]|[\u3130-\u318f]|[\ua960-\ua97f]|[\ud7b0-\ud7ff]|[\u0000-\u00ff]/g,
    error: {
      unsupportedCharacters: true,
    },
  },
  default: {
    regex: /[\u0000-\u00ff]/g,
    error: {
      unsupportedCharacters: true,
    },
  },
};

function checkState(
  country: AddressFormProps['values']['country'],
  state: AddressFormProps['values']['state'],
) {
  const countryRegions = ejsUtils.regions.regionsByCountry[country];
  if (countryRegions) {
    if (!countryRegions.regions.some((region) => region.id === state)) {
      return { invalidRegion: true };
    }
  }

  return null;
}

/**
 * Returns errors as an object of fieldNames as keys and flags to return error types
 */
export function validateAddressFields(
  address: AddressFormProps['values'],
  hasCountriesCharactersRestrictions: AddressFormProps['hasCountriesCharactersRestrictions'],
  requiredFields: AddressFormProps['requiredFields'] = addressFieldsToCheck,
) {
  const errors: ErrorsType = {};
  const { country } = address;

  const pushAddressFieldError = (
    fieldName: FieldNamesType,
    errorObj: ErrorType,
  ) => {
    errors[fieldName] = {
      ...(errors[fieldName] || {}),
      ...(errorObj || {}),
    };
  };

  Object.entries(address).forEach(([fieldName, value]) => {
    if (
      // Object.entries from TS is returning [string, value], so we're not keeping the key/value
      // type. A workaround is to define with `as`
      requiredFields.includes(fieldName as FieldNamesType) &&
      !value?.length
    ) {
      // Object.entries from TS is returning [string, value], so we're not keeping the key/value
      // type. A workaround is to define with `as`
      pushAddressFieldError(fieldName as FieldNamesType, { required: true });
    }
  });

  const houseNumberCheck = /\d/;
  if (country === 'US' && !houseNumberCheck.test(address.address1)) {
    pushAddressFieldError(fieldNames.ADDRESS_1, { houseNumber: true });
  }

  const stateError = checkState(country, address.state);
  if (stateError) {
    pushAddressFieldError(fieldNames.STATE, stateError);
  }
  if (!ejsUtils.zipcode.isZipCodeValid(country, address.zip)) {
    if (!address.zip) {
      // ZIP is empty:
      pushAddressFieldError(fieldNames.ZIPCODE, { required: true });
    } else {
      // ZIP is invalid for country:
      pushAddressFieldError(fieldNames.ZIPCODE, { format: true });
    }
  }

  if (hasCountriesCharactersRestrictions) {
    for (const fieldName of addressFieldsToCheck) {
      const value = address[fieldName] || '';

      if (
        value?.length &&
        value.match(
          countriesCharactersRestrictions[country]?.regex ||
            countriesCharactersRestrictions['default']?.regex,
        )?.length !== value.length
      ) {
        pushAddressFieldError(
          fieldName,
          countriesCharactersRestrictions[country]?.error ||
            countriesCharactersRestrictions.default?.error,
        );
      }
    }
  }

  return errors;
}
